import { useCallback, useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { IkonaKrzyzyk } from './Ikony.js'
import { useWyjscie } from './ruch.js'
import './modal.css'

interface Props {
  tytul: string
  onZamknij(): void
  children: ReactNode
  stopka?: ReactNode
}

const OGNISKOWALNE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Okno modalne z pułapką na fokus. Bez niej klawiatura wychodzi za okno
 * i użytkownik traci orientację, co jest szczególnie dotkliwe przy czytniku
 * ekranu.
 */
export function Modal({ tytul, onZamknij, children, stopka }: Props) {
  const okno = useRef<HTMLDivElement>(null)
  const poprzedniFokus = useRef<Element | null>(null)
  const { zamykanie, rozpocznijZamykanie } = useWyjscie(onZamknij)

  const obsluzKlawisz = useCallback(
    (zdarzenie: KeyboardEvent) => {
      if (zdarzenie.key === 'Escape') {
        zdarzenie.preventDefault()
        rozpocznijZamykanie()
        return
      }
      if (zdarzenie.key !== 'Tab' || okno.current === null) return

      const elementy = [...okno.current.querySelectorAll<HTMLElement>(OGNISKOWALNE)]
      if (elementy.length === 0) return
      const pierwszy = elementy[0]
      const ostatni = elementy[elementy.length - 1]
      if (pierwszy === undefined || ostatni === undefined) return

      if (zdarzenie.shiftKey && document.activeElement === pierwszy) {
        zdarzenie.preventDefault()
        ostatni.focus()
      } else if (!zdarzenie.shiftKey && document.activeElement === ostatni) {
        zdarzenie.preventDefault()
        pierwszy.focus()
      }
    },
    [rozpocznijZamykanie],
  )

  useEffect(() => {
    poprzedniFokus.current = document.activeElement
    document.addEventListener('keydown', obsluzKlawisz)
    const pierwszy = okno.current?.querySelector<HTMLElement>(OGNISKOWALNE)
    pierwszy?.focus()

    const poprzedniOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', obsluzKlawisz)
      document.body.style.overflow = poprzedniOverflow
      if (poprzedniFokus.current instanceof HTMLElement) poprzedniFokus.current.focus()
    }
  }, [obsluzKlawisz])

  return createPortal(
    <div
      className={zamykanie ? 'modal-tlo modal-tlo--zamyka' : 'modal-tlo'}
      onMouseDown={(zdarzenie) => {
        if (zdarzenie.target === zdarzenie.currentTarget) rozpocznijZamykanie()
      }}
    >
      <div
        className={zamykanie ? 'modal modal--zamyka' : 'modal'}
        role="dialog"
        aria-modal="true"
        aria-label={tytul}
        ref={okno}
      >
        <header className="modal__naglowek">
          <h2 className="modal__tytul">{tytul}</h2>
          <button
            type="button"
            className="modal__zamknij"
            onClick={rozpocznijZamykanie}
            aria-label="Zamknij okno"
          >
            <IkonaKrzyzyk width={18} height={18} />
          </button>
        </header>
        <div className="modal__tresc">{children}</div>
        {stopka === undefined ? null : <footer className="modal__stopka">{stopka}</footer>}
      </div>
    </div>,
    document.body,
  )
}
